import React, { createContext, useState, useEffect, useContext } from "react";
import axios from "axios";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Helper function to get the CSRF token from cookies
  const getCsrfToken = () => {
    const csrfCookie = document.cookie
      .split("; ")
      .find((row) => row.startsWith("csrftoken="));
    return csrfCookie ? csrfCookie.split("=")[1] : null;
  };

  // Fetch the current user and handle authentication state
  const fetchUser = async () => {
    console.log("Fetching user...");
    try {
      const csrfToken = getCsrfToken();
      if (!csrfToken) {
        console.error("CSRF token not found. Fetching a new token...");
        await fetchCsrfToken();
      }

      const response = await axios.get("/api/current-user/", {
        withCredentials: true, // Include cookies in request
        headers: { "X-CSRFToken": getCsrfToken() }, // Use updated CSRF token
      });

      console.log("User data fetched:", response.data);
      setUser(response.data);
    } catch (error) {
      console.error("Error fetching current user:", error);
      setUser(null); // Ensure user is set to null on failure
    } finally {
      setLoading(false); // Set loading to false when finished
    }
  };

  // Fetch the CSRF token from the server
  const fetchCsrfToken = async () => {
    try {
      const response = await axios.get("/api/get-csrf-token/", { withCredentials: true });
      console.log("CSRF Token fetched from server.");
    } catch (error) {
      console.error("Error fetching CSRF token:", error);
    }
  };

  // Sign-in function
  const signIn = async (mobileOrEmail, password) => {
    try {
      const csrfToken = getCsrfToken();
      if (!csrfToken) throw new Error("CSRF token is missing");

      const response = await axios.post(
        "/api/signin/",
        { mobileOrEmail, password },
        {
          withCredentials: true, // Include cookies
          headers: { "X-CSRFToken": csrfToken }, // Send CSRF token
        }
      );

      await fetchUser(); // Fetch user after successful login
      return { success: true, message: "Login successful!" };
    } catch (error) {
      console.error("Sign in error:", error);
      return { success: false, message: error.response?.data?.error || "Sign in failed." };
    }
  };

  // Sign-out function
  const signOut = async () => {
    try {
      // Log all cookies for debugging
      console.log("Current cookies:", document.cookie);

      const csrfToken = getCsrfToken(); // Get CSRF token from cookies
      if (!csrfToken) {
        console.error("CSRF token is missing. Attempting to fetch a new CSRF token...");
        await fetchCsrfToken(); // Try fetching a new CSRF token
      }

      // Retry getting the CSRF token after fetching
      const refreshedCsrfToken = getCsrfToken();
      if (!refreshedCsrfToken) {
        throw new Error("CSRF token is still missing after fetching.");
      }

      // Perform the sign-out request
      const response = await axios.post(
        "/api/signout/",
        {},
        {
          headers: { "X-CSRFToken": refreshedCsrfToken },
          withCredentials: true, // Ensure cookies are sent with the request
        }
      );

      console.log("Sign out successful:", response);

      // Clear user state (context or local state)
      setUser(null);
    } catch (error) {
      console.error("Sign out error:", error);
    }
  };


  useEffect(() => {
    const initialize = async () => {
      try {
        // Fetch CSRF token first
        await fetchCsrfToken();
        console.log("CSRF Token fetched successfully.");
  
        // Fetch user after CSRF token is available
        await fetchUser();
        console.log("User data fetched successfully.");
      } catch (error) {
        console.error("Initialization error:", error);
      } finally {
        setLoading(false);
      }
    };
  
    initialize();
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signOut, getCsrfToken }}>
      {children}
    </AuthContext.Provider>
  );
  
};

// Export the useAuthContext hook
export const useAuthContext = () => useContext(AuthContext);
